import React, { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { BookOpen, Headphones, ChevronRight, BarChart3, CheckCircle } from "lucide-react";
import { useAuth } from "../AuthContext";
import { getChapter } from "../data/series";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyLibraryPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [library, setLibrary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authLoading || !user) return;
    const fetchLibrary = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/my-library`);
        setLibrary(res.data);
      } catch { /* ignore */ }
      setIsLoading(false);
    };
    fetchLibrary();
  }, [user, authLoading]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" state={{ from: "/my-library" }} replace />;

  const formatPosition = (seconds) => {
    if (!seconds) return "";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div data-testid="my-library-page" className="min-h-screen bg-ff-bg pt-24 md:pt-32 pb-24">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 mb-12">
        <p className="label text-ff-accent mb-4">YOUR LIBRARY</p>
        <h1 className="font-serif text-4xl md:text-5xl text-ff-text mb-3">My Library</h1>
        <p className="text-ff-text-muted font-light">
          Everything you've been reading and listening to, in one place.
        </p>
      </div>

      {isLoading ? (
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="animate-pulse space-y-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-ff-surface rounded" />
            ))}
          </div>
        </div>
      ) : !library || library.series.length === 0 ? (
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center py-16">
          <BookOpen size={48} className="mx-auto text-ff-text-muted mb-6 opacity-40" />
          <h2 className="font-serif text-2xl text-ff-text mb-3">Nothing here yet</h2>
          <p className="text-ff-text-muted mb-8 max-w-sm mx-auto">
            Start reading a story and it'll appear here so you can pick up right where you left off.
          </p>
          <Link to="/library" data-testid="browse-library-btn" className="btn-primary inline-flex items-center gap-2">
            <BookOpen size={16} /> Browse the Library
          </Link>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Series Started", value: library.stats.series_started, icon: BookOpen },
                { label: "Chapters Read", value: library.stats.chapters_started, icon: BarChart3 },
                { label: "Completed", value: library.stats.chapters_completed, icon: CheckCircle },
                { label: "Listened", value: library.stats.chapters_listened, icon: Headphones },
              ].map((stat) => (
                <div key={stat.label} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`} className="p-4 bg-ff-surface border border-ff-border">
                  <stat.icon size={14} className="text-ff-text-muted mb-2" />
                  <p className="text-2xl font-serif text-ff-text">{stat.value}</p>
                  <p className="text-xs text-ff-text-muted uppercase tracking-wider mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Series Cards */}
          <div className="max-w-4xl mx-auto px-4 md:px-6 space-y-4">
            {library.series.map((s) => {
              const pct = s.total_chapters > 0 ? Math.round((s.chapters_started / s.total_chapters) * 100) : 0;
              const completePct = s.total_chapters > 0 ? Math.round((s.chapters_completed / s.total_chapters) * 100) : 0;
              const lastReadCh = s.last_read_chapter > 0 ? getChapter(s.series_slug, s.last_read_chapter) : null;
              const lastListenCh = s.last_listen_chapter > 0 ? getChapter(s.series_slug, s.last_listen_chapter) : null;

              return (
                <div
                  key={s.series_slug}
                  data-testid={`library-series-${s.series_slug}`}
                  className="bg-ff-surface border border-ff-border overflow-hidden"
                >
                  {/* Series Header */}
                  <div className="p-5 pb-4">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1 min-w-0">
                        <Link to={`/${s.series_slug}`} className="font-serif text-xl text-ff-text hover:underline decoration-1 underline-offset-4" style={{ textDecorationColor: s.accent }}>
                          {s.title}
                        </Link>
                        <p className="text-ff-text-muted text-xs mt-1 uppercase tracking-wider">
                          {s.chapters_started} of {s.total_chapters} chapters started
                          {s.chapters_completed > 0 && ` · ${s.chapters_completed} completed`}
                          {s.chapters_listened > 0 && ` · ${s.chapters_listened} listened`}
                        </p>
                      </div>
                      <span className="text-2xl font-serif shrink-0" style={{ color: s.accent }}>{pct}%</span>
                    </div>

                    {/* Progress Bar */}
                    <div className="h-1.5 bg-ff-border rounded-full overflow-hidden">
                      <div className="h-full rounded-full relative" style={{ width: `${Math.max(pct, 2)}%` }}>
                        <div className="absolute inset-0 rounded-full" style={{ background: `${s.accent}40`, width: '100%' }} />
                        <div className="absolute inset-0 rounded-full" style={{ background: s.accent, width: `${pct > 0 ? (completePct / pct) * 100 : 0}%` }} />
                      </div>
                    </div>
                  </div>

                  {/* Continue Actions */}
                  <div className="flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-ff-border border-t border-ff-border">
                    {lastReadCh && (
                      <Link
                        to={`/${s.series_slug}/chapter-${s.last_read_chapter}`}
                        data-testid={`continue-read-${s.series_slug}`}
                        className="flex items-center gap-3 p-4 flex-1 group hover:bg-ff-bg/50 transition-colors"
                      >
                        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${s.accent}15` }}>
                          <BookOpen size={15} style={{ color: s.accent }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-ff-text-muted">Continue Reading</p>
                          <p className="text-sm text-ff-text truncate">Ch {s.last_read_chapter}: {lastReadCh.title}</p>
                        </div>
                        <ChevronRight size={16} className="text-ff-text-muted shrink-0 group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    )}
                    {lastListenCh && s.last_listen_position > 5 && (
                      <Link
                        to={`/${s.series_slug}/chapter-${s.last_listen_chapter}`}
                        data-testid={`continue-listen-${s.series_slug}`}
                        className="flex items-center gap-3 p-4 flex-1 group hover:bg-ff-bg/50 transition-colors"
                      >
                        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${s.accent}15` }}>
                          <Headphones size={15} style={{ color: s.accent }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-ff-text-muted">Continue Listening</p>
                          <p className="text-sm text-ff-text truncate">
                            Ch {s.last_listen_chapter}: {lastListenCh.title}
                            <span className="text-ff-text-muted ml-1 text-xs">at {formatPosition(s.last_listen_position)}</span>
                          </p>
                        </div>
                        <ChevronRight size={16} className="text-ff-text-muted shrink-0 group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    )}
                    {!lastReadCh && !lastListenCh && (
                      <Link
                        to={`/${s.series_slug}/chapter-1`}
                        data-testid={`start-${s.series_slug}`}
                        className="flex items-center gap-3 p-4 flex-1 group hover:bg-ff-bg/50 transition-colors"
                      >
                        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${s.accent}15` }}>
                          <BookOpen size={15} style={{ color: s.accent }} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-ff-text">Start from Chapter 1</p>
                        </div>
                        <ChevronRight size={16} className="text-ff-text-muted shrink-0 group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Discover More */}
          <div className="max-w-4xl mx-auto px-4 md:px-6 mt-12 text-center">
            <p className="text-ff-text-muted text-sm mb-3">Discover more stories</p>
            <Link to="/library" data-testid="library-browse-more" className="text-ff-accent text-sm hover:underline inline-flex items-center gap-1">
              Browse the full library <ChevronRight size={14} />
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default MyLibraryPage;
